import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Chevron } from "../../../shared/phone/Row";
import { ActionSheet, Sheet, type SheetAction } from "../../../shared/phone/Sheet";
import type { Asset, AssetFolder } from "../../../shared/types";
import { extOf, metaSub, typeLabel, useAssetLibrary, type AssetNote } from "../../components/AssetLibrary";
import { Icon } from "../../components/ui/Icon";
import { api, ApiError } from "../../lib/api";
import { useQueryScreenScroll } from "./queryScreen";
import { plural } from "./text";
import { useToast } from "./useToast";
import { useCan } from "../../context/AuthContext";

type Library = ReturnType<typeof useAssetLibrary>;

/**
 * A file's line: what it is and its size, the folder it is in when that is not on screen, and whether
 * partners see it on its own when its folder does not already share it.
 */
function fileLine(a: Asset, home?: AssetFolder, showFolder = false): string {
  const what = a.type === "copy" ? `Swipe copy, ${metaSub(a)}` : [typeLabel(a.type), metaSub(a).replace(" · ", ", ")].filter(Boolean).join(", ");
  return [what, showFolder && home && `in ${home.name}`, a.sharedWithPartners && !home?.sharedWithPartners && "shared with partners"].filter(Boolean).join(" · ");
}

function download(a: Asset) {
  const link = document.createElement("a");
  link.href = api.assets.downloadUrl(a.id);
  link.download = a.filename ?? a.name;
  link.click();
}

/** A file's picture: the image itself when one is stored, else what kind of file it is. */
function Thumb({ a }: { a: Asset }) {
  const [broken, setBroken] = useState(false);
  // Why: only an uploaded image has a picture to show; a file without one would only fail to load.
  if (a.type === "image" && a.contentType && !broken)
    return (
      <span className="pd-thumb">
        <img src={api.assets.downloadUrl(a.id)} alt="" loading="lazy" onError={() => setBroken(true)} />
      </span>
    );
  if (a.type === "copy")
    return (
      <span className="pd-thumb txt" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </span>
    );
  return (
    <span className={a.type === "video" ? "pd-thumb vid" : "pd-thumb doc"} aria-hidden="true">
      <Icon name={a.type === "video" ? "play" : a.type === "image" ? "photo" : "doc"} />
    </span>
  );
}

function FileRow({ a, home, showFolder, onOpen, onMore }: { a: Asset; home?: AssetFolder; showFolder: boolean; onOpen: () => void; onMore: () => void }) {
  return (
    <div className="pn-row pd-file">
      <button type="button" className="pd-fmain" onClick={onOpen}>
        <Thumb a={a} />
        <span className="m">
          <span className="t">{a.name}</span>
          <span className="s">{fileLine(a, home, showFolder)}</span>
        </span>
      </button>
      <button type="button" className="pd-fdots" aria-label={`More for ${a.name}`} onClick={onMore}>
        <Icon name="more" />
      </button>
    </div>
  );
}

/** A switch row: sharing with partners, in words under it. */
function ShareRow({ on, busy, sub, onToggle }: { on: boolean; busy?: boolean; sub: string; onToggle: () => void }) {
  const may = useCan()("admin");
  return (
    <div className="pn-row static pd-swrow">
      <span className="m">
        <span className="t">Share with partners</span>
        <span className="s">{sub}</span>
      </span>
      <button type="button" className="pn-sw" role="switch" aria-checked={on} aria-label="Share with partners" disabled={busy || !may} onClick={onToggle} />
    </div>
  );
}

/** A new folder: its name, and whether partners see what goes in it. */
function FolderSheet({ onClose, lib, say }: { onClose: () => void; lib: Library; say: (m: string, n?: AssetNote) => void }) {
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      await lib.createFolder(name.trim(), shared);
      onClose();
    } catch (e) {
      setSaving(false);
      say(`Couldn't make the folder: ${e instanceof ApiError ? e.message : String(e)}`, "warn");
    }
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title="New folder"
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      foot={
        <button type="button" className="pn-btn pri wide" disabled={!name.trim() || saving} onClick={() => void submit()}>
          {saving ? "Adding…" : "Add folder"}
        </button>
      }
    >
      <div className="pn-grp">
        <div className="pn-irow col">
          <label htmlFor="pd-folder-name">Name</label>
          <input
            id="pd-folder-name"
            value={name}
            placeholder="Social"
            autoComplete="off"
            enterKeyHint="done"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void submit();
            }}
          />
        </div>
      </div>
      <div className="pn-grp pd-gap">
        <ShareRow on={shared} sub="Every file you put in it is visible to partners." onToggle={() => setShared((s) => !s)} />
      </div>
    </Sheet>
  );
}

/** Swipe copy to write, or to change: its name and the words partners copy. */
function CopySheet({
  edit,
  onClose,
  onSubmit,
  say,
}: {
  edit: Asset | null;
  onClose: () => void;
  onSubmit: (name: string, text: string, shared: boolean) => Promise<void>;
  say: (m: string, n?: AssetNote) => void;
}) {
  const [name, setName] = useState(edit?.name ?? "");
  const [text, setText] = useState(edit?.text ?? "");
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const valid = name.trim().length > 0 && text.trim().length > 0;
  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await onSubmit(name.trim(), text, shared);
      onClose();
    } catch (e) {
      setSaving(false);
      say(`Couldn't save: ${e instanceof ApiError ? e.message : String(e)}`, "warn");
    }
  };
  return (
    <Sheet
      open
      onClose={onClose}
      title={edit ? "Edit swipe copy" : "New swipe copy"}
      left={
        <button type="button" className="pn-tbtn" onClick={onClose}>
          Cancel
        </button>
      }
      foot={
        <button type="button" className="pn-btn pri wide" disabled={!valid || saving} onClick={() => void submit()}>
          {saving ? "Saving…" : edit ? "Save" : "Add copy"}
        </button>
      }
    >
      <div className="pn-grp">
        <div className="pn-irow col">
          <label htmlFor="pd-copy-name">Name</label>
          <input id="pd-copy-name" value={name} placeholder="Launch email" autoComplete="off" onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="pn-irow col">
          <label htmlFor="pd-copy-text">Text</label>
          <textarea id="pd-copy-text" rows={6} value={text} placeholder="The caption or email your partners can copy and paste." onChange={(e) => setText(e.target.value)} />
        </div>
      </div>
      {!edit && (
        <div className="pn-grp pd-gap">
          <ShareRow on={shared} sub={shared ? "Shared with partners." : "Keep private for now."} onToggle={() => setShared((s) => !s)} />
        </div>
      )}
    </Sheet>
  );
}

/** A file, big: the picture, the video, the document or the words, with the one thing to do with it. */
function PreviewSheet({ a, onClose, onCopy }: { a: Asset | null; onClose: () => void; onCopy: (a: Asset) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const id = a?.id;
  const kind = a?.type;
  useEffect(() => {
    if (!id || kind === "copy") return;
    let alive = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setState("loading");
    fetch(api.assets.downloadUrl(id), { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setState("ready");
      })
      .catch(() => alive && setState("error"));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, kind]);

  const isPdf = !!a && (a.contentType === "application/pdf" || extOf(a) === "pdf");
  let body = null;
  if (a?.type === "copy") body = <div className="pn-card pd-copytext">{a.text}</div>;
  else if (state === "loading") body = <p className="pn-note">Opening…</p>;
  else if (state === "error" || !url) body = <div className="pn-empty">This file could not be shown here. Download it to open it.</div>;
  else if (a?.type === "image") body = <img className="pd-pv" src={url} alt={a.name} />;
  else if (a?.type === "video")
    body = (
      <video className="pd-pv" src={url} controls playsInline>
        <track kind="captions" />
      </video>
    );
  else if (isPdf) body = <iframe className="pd-pv pd-pvdoc" src={url} title={a?.name} />;
  else body = <div className="pn-empty">There is no preview for this kind of file. Download it to open it.</div>;

  return (
    <Sheet
      open={!!a}
      onClose={onClose}
      title={a?.name}
      tall
      right={
        <button type="button" className="pn-tbtn strong" onClick={onClose}>
          Done
        </button>
      }
      foot={
        a?.type === "copy" ? (
          <button type="button" className="pn-btn pri wide" onClick={() => onCopy(a)}>
            <Icon name="doc" />
            Copy the text
          </button>
        ) : a ? (
          <button type="button" className="pn-btn pri wide" onClick={() => download(a)}>
            <Icon name="down" />
            Download
          </button>
        ) : null
      }
    >
      <div className="pn-pad pd-pvbox">{body}</div>
      {a && <p className="pn-note">{fileLine(a)}</p>}
    </Sheet>
  );
}

/**
 * A product's Assets on a phone or an iPad: its own screen of folders, each saying whether partners see
 * it, and a folder's own screen with the one switch that shares it and its files. `folderId` picks the
 * folder; both are kept in the address so back and links work.
 */
export function AssetsNative({ productId, productName, folderId }: { productId: string; productName?: string; folderId: string | null }) {
  const { toast, toastNode } = useToast();
  const say = (msg: string, note: AssetNote = "info") => toast(msg, note !== "warn");
  const lib = useAssetLibrary(productId, say);
  const { folders, assets, loading, err, busy } = lib;
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [newFolder, setNewFolder] = useState(false);
  const [copy, setCopy] = useState<{ edit: Asset | null } | null>(null);
  const [menu, setMenu] = useState<Asset | null>(null);
  const [deleting, setDeleting] = useState<Asset | null>(null);
  const [preview, setPreview] = useState<Asset | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const may = useCan()("admin");
  useQueryScreenScroll(folderId ?? "assets");

  const base = `/products/${encodeURIComponent(productId)}`;
  const folder = folderId ? (folders.find((f) => f.id === folderId) ?? null) : null;
  const homeOf = (a: Asset) => folders.find((f) => f.id === a.folderId);
  const inFolder = (f: AssetFolder) => assets.filter((a) => a.folderId === f.id);

  const fileActions = (a: Asset): SheetAction[] => {
    const home = folders.find((f) => f.id === a.folderId);
    const acts: SheetAction[] =
      a.type === "copy"
        ? [
            { label: "Copy the text", onSelect: () => void lib.copyToClipboard(a) },
            ...(may ? [{ label: "Edit", onSelect: () => setCopy({ edit: a }) }] : []),
          ]
        : [
            { label: "Preview", onSelect: () => setPreview(a) },
            { label: "Download", onSelect: () => download(a) },
          ];
    if (!may) return acts;
    if (home?.sharedWithPartners && !a.sharedWithPartners) acts.push({ label: `Shared through ${home.name}`, disabled: true, onSelect: () => undefined });
    else acts.push({ label: a.sharedWithPartners ? "Stop sharing with partners" : "Share with partners", onSelect: () => void lib.toggleAssetShare(a) });
    acts.push({ label: "Delete", danger: true, onSelect: () => setDeleting(a) });
    return acts;
  };

  const row = (a: Asset, showFolder = false) => (
    <FileRow key={a.id} a={a} home={homeOf(a)} showFolder={showFolder} onOpen={() => setPreview(a)} onMore={() => setMenu(a)} />
  );
  const addActions: SheetAction[] = [
    { label: "Upload a file", onSelect: () => fileRef.current?.click() },
    { label: "Write swipe copy", onSelect: () => setCopy({ edit: null }) },
    ...(folderId ? [] : [{ label: "New folder", onSelect: () => setNewFolder(true) }]),
  ];
  const addButton = may && (
    <button type="button" className="pn-tbtn word" onClick={() => setAdding(true)} aria-label={folder ? `Add to ${folder.name}` : "Add to Assets"}>
      <Icon name="plus" />
      <span>Add</span>
    </button>
  );

  let screen;
  if (folderId) {
    const files = folder ? inFolder(folder) : [];
    screen = (
      <div className="pn-screen">
        <PhoneNav title={folder?.name ?? "Folder"} large back={{ to: `${base}?assets`, label: "Assets" }} actions={folder ? addButton : undefined} />
        <div className="pn-stack tight">
          {loading && <p className="pn-note">Loading…</p>}
          {err && <p className="pn-note">Couldn't load the library: {err}</p>}
          {!loading && !err && !folder && (
            <div className="pn-empty">
              <b>This folder is not here</b>
              It may have been removed.
            </div>
          )}
          {folder && (
            <>
              <div className="pn-grp">
                <ShareRow
                  on={folder.sharedWithPartners}
                  busy={busy.has(folder.id)}
                  sub={folder.sharedWithPartners ? "Every file inside is visible to your partners." : "Only you can see these files."}
                  onToggle={() => void lib.toggleFolderShare(folder)}
                />
              </div>
              <section>
                <div className="pn-cap">{plural(files.length, "file")}</div>
                <div className="pn-grp">
                  {files.length ? (
                    files.map((a) => row(a))
                  ) : (
                    <div className="pn-row static">
                      <span className="m">
                        <span className="s">No files in this folder yet.</span>
                      </span>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    );
  } else {
    const q = query.trim().toLowerCase();
    const found = q ? assets.filter((a) => a.name.toLowerCase().includes(q)) : [];
    const loose = assets.filter((a) => a.folderId === null);
    screen = (
      <div className="pn-screen">
        <PhoneNav title="Assets" large back={{ to: base, label: productName ?? "" }} actions={addButton} />
        <div className="pn-stack tight">
          <label className="pn-search">
            <Icon name="search" />
            <input type="search" placeholder="Search assets" aria-label="Search assets" value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          {loading && <p className="pn-note">Loading…</p>}
          {err && <p className="pn-note">Couldn't load the library: {err}</p>}
          {!loading && !err && q && (found.length ? <div className="pn-grp">{found.map((a) => row(a, true))}</div> : <div className="pn-empty">No assets match “{query.trim()}”.</div>)}
          {!loading && !err && !q && (
            <>
              {folders.length > 0 && (
                <div className="pn-grp">
                  {folders.map((f) => (
                    <Link className="pn-row" key={f.id} to={`${base}?assets&folder=${encodeURIComponent(f.id)}`}>
                      <span className={f.sharedWithPartners ? "pn-ico" : "pn-ico plain"}>
                        <Icon name="pdFolder" />
                      </span>
                      <span className="m">
                        <span className="t">{f.name}</span>
                        <span className="s">
                          {plural(inFolder(f).length, "item")} · <span className={f.sharedWithPartners ? "pd-shon" : undefined}>{f.sharedWithPartners ? "Shared with partners" : "Private"}</span>
                        </span>
                      </span>
                      <Chevron />
                    </Link>
                  ))}
                </div>
              )}
              {loose.length > 0 && (
                <section>
                  <div className="pn-cap">Not in a folder</div>
                  <div className="pn-grp">{loose.map((a) => row(a))}</div>
                </section>
              )}
              {folders.length > 0 || loose.length > 0 ? (
                <p className="pn-note">Partners see shared folders in their portal, ready to download.</p>
              ) : (
                <div className="pn-empty">
                  <b>No assets yet</b>
                  Add a file or swipe copy, or make a folder.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {screen}
      <input
        ref={fileRef}
        type="file"
        hidden
        aria-label="A file to add"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void lib.upload(file, folderId);
        }}
      />
      <ActionSheet open={adding} onClose={() => setAdding(false)} title={folder ? `Add to ${folder.name}` : "Add to Assets"} actions={addActions} />
      <ActionSheet open={!!menu} onClose={() => setMenu(null)} title={menu?.name} message={menu ? fileLine(menu, homeOf(menu)) : undefined} actions={menu ? fileActions(menu) : []} />
      <ActionSheet
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={deleting ? `Delete ${deleting.name}?` : undefined}
        message={deleting?.type === "copy" ? "It is gone for good, and partners can no longer copy it." : "It is gone for good, and partners can no longer see or download it."}
        actions={deleting ? [{ label: "Delete", danger: true, onSelect: () => void lib.doDelete(deleting) }] : []}
      />
      {newFolder && <FolderSheet onClose={() => setNewFolder(false)} lib={lib} say={say} />}
      {copy && (
        <CopySheet
          edit={copy.edit}
          onClose={() => setCopy(null)}
          say={say}
          onSubmit={(name, text, shared) => (copy.edit ? lib.saveCopyEdit(copy.edit.id, name, text) : lib.createCopy(name, text, shared, folderId))}
        />
      )}
      <PreviewSheet a={preview} onClose={() => setPreview(null)} onCopy={(a) => void lib.copyToClipboard(a)} />
      {toastNode}
    </>
  );
}
